// Entry point – propojuje stav, render a persistenci.

import {
  getState,
  subscribe,
  update,
  setItem,
  addItem,
  removeItem,
  setDate,
  reset,
  replaceState,
} from './state.js';
import { render, bindEvents } from './render.js';
import {
  loadState,
  saveState,
  clearState,
  nextInvoiceNumber,
  debounce,
} from './storage.js';
import { parseNumber } from './format.js';

const ui = { savedAt: null };

const persist = debounce((state) => {
  if (saveState(state)) {
    ui.savedAt = Date.now();
    render(state, ui);
  }
}, 300);

function init() {
  const stored = loadState();
  if (stored) {
    replaceState(stored);
  } else {
    update({ number: nextInvoiceNumber() });
  }

  bindEvents({
    onField: (path, value) => {
      const patch = buildPathPatch(getState(), path, value);
      update(patch);
    },
    onItemField: (id, field, value) => {
      const parsed = parseItemField(field, value);
      setItem(id, { [field]: parsed });
    },
    onItemAdd: () => addItem(),
    onItemRemove: (id) => removeItem(id),
    onDate: (key, iso) => setDate(key, iso),
    onNumber: (value) => update({ number: value }),
    onPrint: () => window.print(),
    onReset: () => {
      const ok = window.confirm(
        'Opravdu vytvořit novou prázdnou fakturu?\nAktuální údaje budou ztraceny.'
      );
      if (!ok) return;
      clearState();
      reset(nextInvoiceNumber());
    },
    onExport: () => exportJson(getState()),
    onImport: (file) => importJson(file).then((data) => data && replaceState(data)),
  });

  subscribe((state) => {
    render(state, ui);
    persist(state);
  });

  render(getState(), ui);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

/**
 * Slozi patch pro update() z tecky-oddelene cesty (napr. "dodavatel.name").
 * @param {object} state
 * @param {string} path
 * @param {string} value
 */
function buildPathPatch(state, path, value) {
  const parts = path.split('.');
  if (parts.length === 1) {
    return { [parts[0]]: value };
  }
  const [head, ...rest] = parts;
  const sub = { ...(state[head] ?? {}) };
  let cur = sub;
  for (let i = 0; i < rest.length - 1; i += 1) {
    cur[rest[i]] = { ...(cur[rest[i]] ?? {}) };
    cur = cur[rest[i]];
  }
  cur[rest[rest.length - 1]] = value;
  return { [head]: sub };
}

function parseItemField(field, raw) {
  if (field === 'qty' || field === 'price') {
    const n = parseNumber(raw);
    return Number.isFinite(n) ? n : 0;
  }
  if (field === 'vat') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }
  return raw;
}

function exportJson(state) {
  const blob = new Blob([JSON.stringify(state, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `faktura-${state.number || 'export'}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function importJson(file) {
  try {
    const text = await file.text();
    return JSON.parse(text);
  } catch (err) {
    window.alert('Soubor se nepodařilo načíst – není to platný JSON.');
    return null;
  }
}
